import { useState, useEffect, useRef } from 'react';
import { getOrGenerateAICitations, AICitationsData } from '../services/aiCitationsService';

interface UseAICitationsReturn {
  citations: AICitationsData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch AI citations for a prospect
 * Automatically generates citations if they don't exist
 */
export function useAICitations(prospectId: string | undefined): UseAICitationsReturn {
  const [citations, setCitations] = useState<AICitationsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchedIdRef = useRef<string | null>(null);
  const isFetchingRef = useRef<boolean>(false);

  const fetchCitations = async () => {
    if (!prospectId) {
      setIsLoading(false);
      return;
    }

    // Prevent duplicate fetches for the same prospectId
    if (fetchedIdRef.current === prospectId || isFetchingRef.current) {
      return;
    }

    fetchedIdRef.current = prospectId;
    isFetchingRef.current = true;

    try {
      setIsLoading(true);
      setError(null);

      const data = await getOrGenerateAICitations(prospectId);
      setCitations(data);
    } catch (err) {
      console.error('Error fetching AI citations:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch citations');

      // Set default values on error
      setCitations({
        google: { citations: 0, pages: 0 },
        chatgpt: { citations: 0, pages: 0 },
        perplexity: { citations: 0, pages: 0 },
        gemini: { citations: 0, pages: 0 },
        copilot: { citations: 0, pages: 0 },
        grok: { citations: 0, pages: 0 },
      });
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    // Reset refs when prospectId changes
    if (fetchedIdRef.current !== prospectId) {
      fetchedIdRef.current = null;
      isFetchingRef.current = false;
    }

    fetchCitations();
  }, [prospectId]);

  return {
    citations,
    isLoading,
    error,
    refetch: fetchCitations,
  };
}
